// src/components/ui/EquiposForm.jsx
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Button } from "./button";
import { Card, CardContent } from "./card";
import { toast } from "react-toastify";

import SelectField from "./SelectField";
import InputField from "./InputField";

// ✔ Constantes SIN CAMBIOS
const Propiedades = [
  "Propio",
  "Comodato",
  "Activo",
  "Arrendado",
  "Mantenimiento",
  "Dado de baja",
];
const Condiciones = ["Nuevo", "Usado"];
const Garantias = ["Si", "No"];

const EquiposForm = ({ equipo = null, onClose, onSave }) => {
  const [form, setForm] = useState({
    cliente: "",
    sede: "",
    area: "",
    categoria: "",
    marca: "",
    modelo: "",
    proveedor: "",
    serial: "",
    inventario: "",
    costo: "",
    fecha_compra: "",
    propiedad: "Propio",
    condicion: "Nuevo",
    garantia: "Si",
    caracteristicas: {},
  });

  const [clientes, setClientes] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [areas, setAreas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [caracteristicasDef, setCaracteristicasDef] = useState([]);

  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem("token");
  const API = process.env.REACT_APP_API_URL;

  /* ---------------------------------------
      CARGA INICIAL
  --------------------------------------- */
  const fetchBasics = useCallback(async () => {
    try {
      const [cRes, aRes, catRes, pRes] = await Promise.all([
        axios.get(`${API}/clientes`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/areas`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/cat`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/prov`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setClientes(cRes.data);
      setAreas(aRes.data);
      setCategorias(catRes.data);
      setProveedores(pRes.data);
    } catch {
      toast.error("Error cargando datos iniciales");
    }
  }, [API, token]);

  useEffect(() => {
    fetchBasics();
  }, [fetchBasics]);

  /* ---------------------------------------
      FETCH DEPENDIENTES
  --------------------------------------- */
  const fetchSedes = async (clienteId) => {
    if (!clienteId) return setSedes([]);
    try {
      const res = await axios.get(`${API}/sedes/${clienteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSedes(res.data);
    } catch {
      toast.error("Error cargando sedes");
    }
  };

  const fetchMarcasPorCategoria = async (categoriaId) => {
    if (!categoriaId) {
      setMarcas([]);
      setModelos([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/marcas/categoria/${categoriaId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMarcas(res.data);
      setModelos([]);
    } catch {
      toast.error("Error cargando marcas");
    }
  };

  const fetchModelosPorMarcaCategoria = async (marcaId, categoriaId) => {
    if (!marcaId || !categoriaId) return setModelos([]);
    try {
      const res = await axios.get(`${API}/models/${marcaId}/${categoriaId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setModelos(res.data);
    } catch {
      toast.error("Error cargando modelos");
    }
  };

  const fetchCaracteristicasPorGrupo = async (grupo) => {
    try {
      const res = await axios.get(
        `${API}/categoria-caracteristicas/grupo/${grupo}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setCaracteristicasDef(res.data);
    } catch {
      setCaracteristicasDef([]);
    }
  };

  /* ---------------------------------------
      EDICIÓN
  --------------------------------------- */
  useEffect(() => {
    if (!equipo) return;

    const categoriaId = equipo.categoria?._id || equipo.categoria;
    const marcaId = equipo.marca?._id || equipo.marca;

    setForm({
      cliente: equipo.cliente?._id || "",
      sede: equipo.sede?._id || "",
      area: equipo.area?._id || "",
      categoria: categoriaId || "",
      marca: marcaId || "",
      modelo: equipo.modelo?._id || "",
      proveedor: equipo.proveedor?._id || "",
      serial: equipo.serial || "",
      inventario: equipo.inventario || "",
      costo: equipo.costo || "",
      fecha_compra: equipo.fecha_compra ? equipo.fecha_compra.slice(0, 10) : "",
      propiedad: equipo.propiedad || "Propio",
      condicion: equipo.condicion || "Nuevo",
      garantia: equipo.garantia || "Si",
      caracteristicas: equipo.caracteristicas || {},
    });

    if (equipo.cliente) fetchSedes(equipo.cliente._id || equipo.cliente);
    if (categoriaId) fetchMarcasPorCategoria(categoriaId);
    if (marcaId && categoriaId)
      fetchModelosPorMarcaCategoria(marcaId, categoriaId);
    // eslint-disable-next-line
  }, [equipo]);

  useEffect(() => {
    if (!equipo || !categorias.length) return;

    const categoriaId = equipo.categoria?._id || equipo.categoria;
    const categoriaObj = categorias.find((c) => c._id === categoriaId);
    const grupo = categoriaObj?.grupo;

    if (grupo) {
      fetchCaracteristicasPorGrupo(grupo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipo, categorias]);

  /* ---------------------------------------
      HANDLER ÚNICO
  --------------------------------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;

    // 🔹 CAMPOS DINÁMICOS: caracteristicas.xxx
    if (name.startsWith("caracteristicas.")) {
      const key = name.replace("caracteristicas.", "");

      setForm((prev) => ({
        ...prev,
        caracteristicas: {
          ...(prev.caracteristicas || {}),
          [key]: value,
        },
      }));

      return;
    }

    // 🔹 CAMPOS NORMALES
    setForm((prev) => {
      const updated = { ...prev, [name]: value };

      if (name === "cliente") {
        updated.sede = "";
        fetchSedes(value);
      }

      if (name === "categoria") {
        updated.marca = "";
        updated.modelo = "";
        fetchMarcasPorCategoria(value);

        const categoriaObj = categorias.find((c) => c._id === value);
        const grupo = categoriaObj?.grupo;

        if (grupo) {
          axios
            .get(`${API}/categoria-caracteristicas/grupo/${grupo}`, {
              headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => setCaracteristicasDef(res.data))
            .catch(() => setCaracteristicasDef([]));
        }
      }

      if (name === "marca") {
        updated.modelo = "";
        fetchModelosPorMarcaCategoria(value, updated.categoria);
      }

      return updated;
    });
  };

  /* ---------------------------------------
      SUBMIT
  --------------------------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSave({
        ...form,
        caracteristicas: form.caracteristicas,
        costo: form.costo ? Number(form.costo) : undefined,
      });
    } finally {
      setLoading(false);
    }
  };

  /* ---------------------------------------
      UI
  --------------------------------------- */
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <Card className="border-none shadow-none">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold mb-4">
              {equipo ? "Editar equipo" : "Nuevo equipo"}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <SelectField
                  label="Cliente *"
                  name="cliente"
                  value={form.cliente}
                  onChange={handleChange}
                  options={clientes}
                  displayFn={(c) => c.name}
                />
                <SelectField
                  label="Sede *"
                  name="sede"
                  value={form.sede}
                  onChange={handleChange}
                  options={sedes}
                  displayFn={(s) => s.name}
                />
                <SelectField
                  label="Área *"
                  name="area"
                  value={form.area}
                  onChange={handleChange}
                  options={areas}
                  displayFn={(a) => a.name}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <SelectField
                  label="Categoría *"
                  name="categoria"
                  value={form.categoria}
                  onChange={handleChange}
                  options={categorias}
                  displayFn={(c) => c.name}
                />
                <SelectField
                  label="Marca *"
                  name="marca"
                  value={form.marca}
                  onChange={handleChange}
                  options={marcas}
                  displayFn={(m) => m.name}
                />
                <SelectField
                  label="Modelo *"
                  name="modelo"
                  value={form.modelo}
                  onChange={handleChange}
                  options={modelos}
                  displayFn={(m) => m.name}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <SelectField
                  label="Proveedor"
                  name="proveedor"
                  value={form.proveedor}
                  onChange={handleChange}
                  options={proveedores}
                  displayFn={(p) => p.name}
                />
                <InputField
                  label="Serial *"
                  name="serial"
                  value={form.serial}
                  onChange={handleChange}
                />
                <InputField
                  label="Inventario"
                  name="inventario"
                  value={form.inventario}
                  onChange={handleChange}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <InputField
                  label="Costo"
                  name="costo"
                  type="number"
                  value={form.costo}
                  onChange={handleChange}
                />
                <InputField
                  label="Fecha de compra"
                  name="fecha_compra"
                  type="date"
                  value={form.fecha_compra}
                  onChange={handleChange}
                />
                <SelectField
                  label="Propiedad *"
                  name="propiedad"
                  value={form.propiedad}
                  onChange={handleChange}
                  options={Propiedades.map((p) => ({ _id: p, name: p }))}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <SelectField
                  label="Condición *"
                  name="condicion"
                  value={form.condicion}
                  onChange={handleChange}
                  options={Condiciones.map((c) => ({ _id: c, name: c }))}
                />
                <SelectField
                  label="Garantía *"
                  name="garantia"
                  value={form.garantia}
                  onChange={handleChange}
                  options={Garantias.map((g) => ({ _id: g, name: g }))}
                />
                <InputField
                  label="Código"
                  readOnly
                  value={equipo?.codigo || "Se genera automáticamente"}
                />
                <InputField
                  label="Fecha registro"
                  readOnly
                  value={
                    equipo && equipo.fecha_registro
                      ? new Date(equipo.fecha_registro).toLocaleString()
                      : new Date().toLocaleString()
                  }
                />
              </div>

              {caracteristicasDef.length > 0 && (
                <>
                  <hr />
                  <h3 className="font-semibold">Características especiales</h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {caracteristicasDef.map((c) => {
                      const name = `caracteristicas.${c.key}`;
                      const value = form.caracteristicas?.[c.key] ?? "";

                      // SELECT
                      if (c.tipo === "select") {
                        return (
                          <SelectField
                            key={c._id}
                            label={c.nombre}
                            name={name}
                            value={value}
                            onChange={handleChange}
                            options={c.opciones.map((o) => ({
                              _id: o,
                              name: o,
                            }))}
                          />
                        );
                      }

                      // BOOLEAN
                      if (c.tipo === "boolean") {
                        return (
                          <SelectField
                            key={c._id}
                            label={c.nombre}
                            name={name}
                            value={value}
                            onChange={handleChange}
                            options={[
                              { _id: true, name: "Sí" },
                              { _id: false, name: "No" },
                            ]}
                          />
                        );
                      }

                      // DATE
                      if (c.tipo === "date") {
                        return (
                          <InputField
                            key={c._id}
                            label={c.nombre}
                            name={name}
                            type="date"
                            value={value}
                            onChange={handleChange}
                          />
                        );
                      }

                      // TEXT / NUMBER
                      return (
                        <InputField
                          key={c._id}
                          label={c.nombre}
                          name={name}
                          type={c.tipo === "number" ? "number" : "text"}
                          value={value}
                          onChange={handleChange}
                        />
                      );
                    })}
                  </div>
                </>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  onClick={onClose}
                  className="bg-red-500 text-white"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-blue-600 text-white"
                >
                  {loading ? "Guardando..." : equipo ? "Actualizar" : "Crear"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EquiposForm;
